import privacyFr from './privacyPolicy/fr';
import privacyEs from './privacyPolicy/es';
import privacyAr from './privacyPolicy/ar';

/** @typedef {{ type: 'p', text: string } | { type: 'ul', items: string[] }} PrivacyBlock */
/** @typedef {{ title: string, blocks: PrivacyBlock[] }} PrivacySection */
/** @typedef {{ title: string, subtitle: string, lastUpdated: string, back: string, sections: PrivacySection[] }} PrivacyDocument */

const privacyEn = {
    title: 'Privacy Policy of DeMaxtore.com',
    subtitle:
      'This Privacy Policy explains how Demakstore Gıda İhracat San. ve Tic. Ltd. Şti., operating the DeMaxtore brand, collects and processes personal and business data.',
    lastUpdated: 'Last updated: 26 May 2026',
    back: '← Back to home',
    sections: [
      {
        title: '1. About DeMaxtore',
        blocks: [
          {
            type: 'p',
            text: 'DeMaxtore is the brand and digital platform operated by Demakstore Gıda İhracat San. ve Tic. Ltd. Şti.',
          },
          {
            type: 'p',
            text: 'DeMaxtore is a B2B platform that helps international buyers source products from verified Turkish manufacturers, compare freight offers, manage inspection processes, and track shipments from factory pickup to warehouse delivery.',
          },
          { type: 'p', text: 'Our services may include, but are not limited to:' },
          {
            type: 'ul',
            items: [
              'Supplier directory and manufacturer listing services',
              'Buyer inquiry and supplier communication tools',
              'CommodityBid sourcing and bidding processes',
              'FreightIQ freight comparison services',
              'Inspection coordination',
              'Live shipment tracking',
              'Import process management and related support services',
            ],
          },
        ],
      },
      {
        title: '2. Personal Data We Collect',
        blocks: [
          {
            type: 'p',
            text: 'We may collect personal data directly from users, companies, suppliers, buyers, logistics providers, or other business partners when they use our website or services.',
          },
          { type: 'p', text: 'The personal data we may collect includes:' },
          {
            type: 'ul',
            items: [
              'Full name',
              'Company name',
              'Job title or department',
              'Business email address',
              'Phone number',
              'Country and city',
              'Company website',
              'Product sourcing requests',
              'Supplier or manufacturer information',
              'Freight, shipment, and delivery-related information',
              'Inspection request details',
              'Messages, inquiries, forms, and communication records',
              'Account login information, if applicable',
              'IP address, browser type, device information, and website usage data',
            ],
          },
          {
            type: 'p',
            text: 'We generally process information in a B2B context and do not intentionally collect sensitive personal data unless it is voluntarily provided by the user and necessary for a specific service.',
          },
        ],
      },
      {
        title: '3. How We Collect Personal Data',
        blocks: [
          { type: 'p', text: 'We may collect personal data when you:' },
          {
            type: 'ul',
            items: [
              'Visit www.demaxtore.com',
              'Fill out a contact form',
              'Submit a sourcing request',
              'Create or use a buyer or supplier account',
              'Use CommodityBid',
              'Use FreightIQ',
              'Request freight comparison',
              'Request inspection services',
              'Request shipment tracking support',
              'Contact us by email, phone, WhatsApp, or other communication channels',
              'Subscribe to updates, newsletters, or commercial communications',
              'Communicate with DeMaxtore representatives',
            ],
          },
          {
            type: 'p',
            text: 'We may also receive business information from suppliers, manufacturers, logistics partners, inspection partners, or publicly available business sources where legally permitted.',
          },
        ],
      },
      {
        title: '4. Purposes of Processing Personal Data',
        blocks: [
          { type: 'p', text: 'We process personal data for the following purposes:' },
          {
            type: 'ul',
            items: [
              'To provide access to Demaxtore’s website and services',
              'To manage buyer and supplier inquiries',
              'To connect buyers with relevant suppliers or manufacturers',
              'To process sourcing requests through CommodityBid',
              'To compare freight offers through FreightIQ',
              'To coordinate inspection and shipment tracking services',
              'To communicate with users, buyers, suppliers, and logistics partners',
              'To provide customer support',
              'To verify company or supplier information',
              'To improve our website, services, and user experience',
              'To send business, service, or marketing communications',
              'To prevent fraud, misuse, spam, or unauthorized activity',
              'To comply with legal, contractual, regulatory, or tax obligations',
              'To protect the rights, safety, reputation, and legitimate interests of DeMaxtore',
            ],
          },
        ],
      },
      {
        title: '5. Legal Basis for Processing',
        blocks: [
          {
            type: 'p',
            text: 'Where applicable under GDPR or similar data protection laws, we process personal data based on one or more of the following legal grounds:',
          },
          {
            type: 'ul',
            items: [
              'Performance of a contract: when processing is necessary to provide our services',
              'Legitimate interest: for B2B communication, service improvement, fraud prevention, sourcing coordination, logistics coordination, and business development',
              'Consent: where users give consent for marketing communications, cookies, or optional communications',
              'Legal obligation: where processing is required by applicable law, regulation, court order, or public authority request',
            ],
          },
          {
            type: 'p',
            text: 'Users may withdraw consent at any time where processing is based on consent.',
          },
        ],
      },
      {
        title: '6. Sharing of Personal Data',
        blocks: [
          {
            type: 'p',
            text: 'In order to provide our services, we may share relevant personal or business data with:',
          },
          {
            type: 'ul',
            items: [
              'Verified suppliers and manufacturers',
              'Buyers who submit sourcing requests',
              'Freight forwarders and logistics partners',
              'Inspection service providers',
              'Payment, accounting, legal, or compliance service providers',
              'IT infrastructure, hosting, CRM, email, analytics, and cloud service providers',
              'Business consultants or authorized DeMaxtore representatives',
              'Public authorities, courts, or regulators where legally required',
            ],
          },
          {
            type: 'p',
            text: 'For example, when a buyer submits a sourcing request, certain business contact details and request details may be shared with relevant suppliers or service partners to facilitate the sourcing process.',
          },
          { type: 'p', text: 'We do not sell personal data to third parties.' },
        ],
      },
      {
        title: '7. Supplier Directory and Company Profiles',
        blocks: [
          {
            type: 'p',
            text: 'Demaxtore may display company-related information in its supplier directory or platform interface. This may include:',
          },
          {
            type: 'ul',
            items: [
              'Company name',
              'Product categories',
              'Country or city',
              'Business description',
              'Company website',
              'Contact or inquiry options',
              'Verification status',
              'Relevant business details provided by the company',
            ],
          },
          {
            type: 'p',
            text: 'Companies are responsible for ensuring that the information they provide is accurate, lawful, up to date, and does not infringe the rights of any third party.',
          },
        ],
      },
      {
        title: '8. Messaging, Contact Forms, and Inquiries',
        blocks: [
          {
            type: 'p',
            text: 'When users send inquiries, messages, or sourcing requests through DeMaxtore, the content of these communications may be reviewed by DeMaxtore’s team for operational, support, quality, compliance, and service management purposes.',
          },
          {
            type: 'p',
            text: 'Messages may also be shared with relevant suppliers, buyers, logistics providers, inspection partners, or other parties involved in the requested service.',
          },
          {
            type: 'p',
            text: 'Users must not submit unlawful, misleading, offensive, fraudulent, spam-related, or third-party-infringing content through our website or services.',
          },
        ],
      },
      {
        title: '9. CommodityBid Data',
        blocks: [
          { type: 'p', text: 'When using CommodityBid, we may process information such as:' },
          {
            type: 'ul',
            items: [
              'Buyer sourcing requests',
              'Product specifications',
              'Quantity, packaging, and delivery expectations',
              'Target price or bidding details',
              'Supplier offers',
              'Company and contact information',
              'Communication records related to the bidding process',
            ],
          },
          {
            type: 'p',
            text: 'This data is used to manage structured B2B bidding processes between buyers and verified suppliers or manufacturers.',
          },
        ],
      },
      {
        title: '10. FreightIQ and Shipment Data',
        blocks: [
          {
            type: 'p',
            text: 'When using FreightIQ, freight comparison, or shipment tracking services, we may process data such as:',
          },
          {
            type: 'ul',
            items: [
              'Pickup and delivery details',
              'Shipment type',
              'Product or cargo information',
              'Freight requirements',
              'Forwarder offers',
              'Tracking updates',
              'Customs or documentation-related business information',
              'Contact details of buyers, suppliers, and logistics partners',
            ],
          },
          {
            type: 'p',
            text: 'This data is used to compare freight options, coordinate logistics, and support door-to-door shipment visibility.',
          },
        ],
      },
      {
        title: '11. Inspection Services Data',
        blocks: [
          { type: 'p', text: 'When inspection services are requested, we may process:' },
          {
            type: 'ul',
            items: [
              'Product details',
              'Factory or supplier information',
              'Inspection location',
              'Inspection requirements',
              'Photos, reports, or quality control documents',
              'Contact details of relevant parties',
            ],
          },
          {
            type: 'p',
            text: 'This information is used to coordinate inspection services and communicate inspection results to the relevant business parties.',
          },
        ],
      },
      {
        title: '12. WhatsApp Business and Other Communication Channels',
        blocks: [
          {
            type: 'p',
            text: 'DeMaxtore may use WhatsApp Business, email, phone, SMS, or other communication channels to communicate with users, buyers, suppliers, and business partners.',
          },
          {
            type: 'p',
            text: 'If you contact us through WhatsApp or another third-party communication tool, your communication may also be subject to the privacy policy of that third-party provider.',
          },
          {
            type: 'p',
            text: 'We may store communication records for business, support, legal, or service quality purposes unless deletion is requested and no legal retention obligation applies.',
          },
        ],
      },
      {
        title: '13. Cookies and Similar Technologies',
        blocks: [
          { type: 'p', text: 'DeMaxtore may use cookies and similar technologies to:' },
          {
            type: 'ul',
            items: [
              'Ensure website functionality',
              'Improve user experience',
              'Analyze website traffic',
              'Remember user preferences',
              'Support security and fraud prevention',
              'Measure marketing performance',
            ],
          },
          {
            type: 'p',
            text: 'Users may manage or disable cookies through their browser settings. Some website features may not function properly if cookies are disabled.',
          },
        ],
      },
      {
        title: '14. Marketing Communications',
        blocks: [
          {
            type: 'p',
            text: 'We may contact users and business representatives by email, phone, WhatsApp, or other communication channels for service-related or commercial purposes, where permitted by law.',
          },
          {
            type: 'p',
            text: 'Users may opt out of marketing communications at any time by contacting us or using the unsubscribe option where available.',
          },
          {
            type: 'p',
            text: 'Even after opting out, we may still send service-related, transactional, legal, or security communications where necessary.',
          },
        ],
      },
      {
        title: '15. International Data Transfers',
        blocks: [
          {
            type: 'p',
            text: 'Because DeMaxtore operates in an international B2B trade environment, personal data may be transferred to or accessed from countries outside the user’s country of residence.',
          },
          {
            type: 'p',
            text: 'Where required by applicable law, we take appropriate safeguards to protect personal data during international transfers, including contractual, technical, and organizational measures.',
          },
        ],
      },
      {
        title: '16. Data Retention',
        blocks: [
          {
            type: 'p',
            text: 'We retain personal data only for as long as necessary for the purposes described in this Privacy Policy, including:',
          },
          {
            type: 'ul',
            items: [
              'Providing services',
              'Managing business relationships',
              'Handling sourcing, logistics, bidding, and inspection processes',
              'Complying with legal and tax obligations',
              'Resolving disputes',
              'Preventing fraud or misuse',
              'Maintaining business records',
            ],
          },
          {
            type: 'p',
            text: 'When personal data is no longer required, we delete, anonymize, or securely archive it in accordance with applicable laws and internal retention practices.',
          },
        ],
      },
      {
        title: '17. Data Security',
        blocks: [
          {
            type: 'p',
            text: 'We take reasonable technical and organizational measures to protect personal data against:',
          },
          {
            type: 'ul',
            items: [
              'Unauthorized access',
              'Accidental loss',
              'Misuse',
              'Alteration',
              'Disclosure',
              'Destruction',
              'Security incidents',
            ],
          },
          {
            type: 'p',
            text: 'However, no internet-based system can be guaranteed to be completely secure. Users are responsible for keeping their account login details confidential and notifying us immediately in case of unauthorized access or suspected security issues.',
          },
        ],
      },
      {
        title: '18. User Rights',
        blocks: [
          {
            type: 'p',
            text: 'Depending on applicable data protection laws, users may have the right to:',
          },
          {
            type: 'ul',
            items: [
              'Access their personal data',
              'Request correction of inaccurate data',
              'Request deletion of personal data',
              'Object to certain processing activities',
              'Restrict processing',
              'Withdraw consent',
              'Request data portability',
              'Lodge a complaint with a competent data protection authority',
            ],
          },
          {
            type: 'p',
            text: 'To exercise these rights, users may contact DeMaxtore using the contact details provided below.',
          },
        ],
      },
      {
        title: '19. Third-Party Websites and Services',
        blocks: [
          {
            type: 'p',
            text: 'Our website may contain links to third-party websites, platforms, logistics providers, suppliers, manufacturers, or service providers.',
          },
          {
            type: 'p',
            text: 'DeMaxtore is not responsible for the privacy practices, content, security, or policies of third-party websites or services. Users should review the privacy policies of those third parties before sharing personal data with them.',
          },
        ],
      },
      {
        title: '20. Children’s Privacy',
        blocks: [
          {
            type: 'p',
            text: 'DeMaxtore’s services are intended for business users and are not directed to children. We do not knowingly collect personal data from individuals under the age of 18.',
          },
          {
            type: 'p',
            text: 'If we become aware that we have collected personal data from a child without appropriate authorization, we will take reasonable steps to delete such data.',
          },
        ],
      },
      {
        title: '21. Changes to This Privacy Policy',
        blocks: [
          {
            type: 'p',
            text: 'DeMaxtore may update this Privacy Policy from time to time to reflect changes in our services, legal requirements, business operations, or data processing practices.',
          },
          {
            type: 'p',
            text: 'The updated version will be published on www.demaxtore.com with the revised “Last Updated” date. Continued use of our website or services after publication of changes means that users acknowledge the updated Privacy Policy.',
          },
        ],
      },
      {
        title: '22. Contact',
        blocks: [
          {
            type: 'p',
            text: 'For questions, requests, or complaints regarding this Privacy Policy or the processing of personal data, please contact us:',
          },
          {
            type: 'ul',
            items: [
              'Demakstore Gıda İhracat San. ve Tic. Ltd. Şti.',
              'Operating Brand: DeMaxtore',
              'Website: www.demaxtore.com',
              'Email: support@demaxtore.com',
              'Alternative Email: demaxtore@demaxtore.com',
            ],
          },
        ],
      },
    ],
};

/** @type {Record<'en'|'fr'|'es'|'ar', PrivacyDocument>} */
export const PRIVACY_POLICY_BY_LANG = {
  en: privacyEn,
  fr: privacyFr,
  es: privacyEs,
  ar: privacyAr,
};
